import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Phone, MessageSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: 'Message Sent!',
          description: response.data.message,
        });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          subject: '',
          message: '',
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again or contact us directly at info@fxcvantage.com',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={true} />

      {/* Hero Section */}
      <section className="relative bg-[#0d6571] pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white hover:text-white/80 mb-8 -ml-4"
          >
            <ArrowLeft className="mr-2" />
            Back to Home
          </Button>

          <div className="flex items-center gap-4 mb-6">
            <MessageSquare className="w-16 h-16 text-[#d97841]" />
            <h1 className="text-5xl md:text-6xl font-bold text-white">Contact Us</h1>
          </div>

          <p className="text-xl text-white/90 max-w-4xl leading-relaxed">
            Get in touch with our team. We're here to help you with any questions or inquiries.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Send us a message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0 transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0 transition-colors"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0 transition-colors"
                    placeholder="john.doe@example.com"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0 transition-colors"
                    placeholder="How can we help you?"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0d6571] focus:ring-0 transition-colors resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-8 py-6 text-lg font-semibold transition-all duration-300 group"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in touch</h2>
              <p className="text-gray-600 leading-relaxed mb-8">
                Feel free to reach out to us through any of the following methods. Our team is
                ready to assist you with your inquiries.
              </p>

              <div className="space-y-6">
                {/* Address */}
                <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-6 rounded-r-lg">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0d6571] p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Post Address</h3>
                      <p className="text-gray-700">
                        <strong>FXC Management Services B.V.</strong>
                      </p>
                      <p className="text-gray-700">Industrieweg 9, 2254AE</p>
                      <p className="text-gray-700">Voorschoten, The Netherlands</p>
                    </div>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="bg-gradient-to-br from-[#d97841]/5 to-[#d97841]/10 border-l-4 border-[#d97841] p-6 rounded-r-lg">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#d97841] p-3 rounded-lg">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">WhatsApp</h3>
                      <a
                        href="https://wa.me/31850470021"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#d97841] hover:underline text-lg font-semibold"
                      >
                        +31 085 047 0021
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-gradient-to-br from-[#0d6571]/5 to-[#0d6571]/10 border-l-4 border-[#0d6571] p-6 rounded-r-lg">
                  <div className="flex items-start gap-4">
                    <div className="bg-[#0d6571] p-3 rounded-lg">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">E-mail</h3>
                      <a
                        href="mailto:info@fxcvantage.com"
                        className="text-[#0d6571] hover:underline text-lg font-semibold"
                      >
                        info@fxcvantage.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-8 bg-gray-100 rounded-xl overflow-hidden h-64 flex items-center justify-center border-2 border-gray-200">
                <div className="text-center">
                  <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 font-semibold">Voorschoten, The Netherlands</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
